import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import BrandLogo from "../components/BrandLogo";
import BrandAttribution from "../components/BrandAttribution";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Register() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "delegate",
    organization_id: "",
  });
  const [orgs, setOrgs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    let cancelled = false;
    // Fetch public orgs to populate the (optional) organisation picker.
    //
    // Two important things this useEffect handles correctly:
    //   1. Dedupe by `organization_id` BEFORE firing requests — events from
    //      the same org used to fan out 1 request per event, hammering the
    //      backend with 10-20 duplicate calls on every page load.
    //   2. Tolerate 404s silently. The `/api/orgs/public/{slug_or_id}` route
    //      now accepts slug or id, so 404s here mean the org genuinely isn't
    //      public-listable — drop them from the dropdown, don't toast.
    api.get("/events/public").then(async (r) => {
      const uniqueOrgIds = [...new Set(r.data.map((e) => e.organization_id).filter(Boolean))];
      const rawOrgs = await Promise.all(
        uniqueOrgIds.map((orgId) =>
          api
            .get(`/orgs/public/${orgId}`)
            .then((x) => x.data)
            .catch(() => null)
        )
      );
      if (!cancelled) {
        // Final safety net — dedupe by id in case the backend returned
        // the same org under two different slug aliases.  Prevents
        // React's "two children with the same key" warning.
        const seen = new Set();
        const deduped = (rawOrgs.filter(Boolean)).filter((o) => {
          if (!o?.id || seen.has(o.id)) return false;
          seen.add(o.id);
          return true;
        });
        setOrgs(deduped);
      }
    }).catch(() => { /* /events/public optional — picker stays empty */ });
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = { ...form };
    if (!payload.organization_id) delete payload.organization_id;
    const res = await register(payload);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Account created — welcome!");
      // Preserve full URL (path + query + hash) when a user landed here from
      // /checkout?plan=...&currency=HKD via a "from" route state, or via a
      // ?next= query string on /register.
      const from = loc.state?.from;
      const nextParam = new URLSearchParams(loc.search).get("next");
      const target = from
        ? `${from.pathname || ""}${from.search || ""}${from.hash || ""}`
        : (nextParam || "/dashboard");
      nav(target || "/dashboard", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="flex items-center justify-center p-8 sm:p-12 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <BrandLogo />
          <p className="mt-10 text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Create account
          </p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
            Join the global floor.
          </h1>
          <p className="mt-3 text-sm text-slate-500">
            Already have an account?{" "}
            <Link to="/login" className="text-[#0F4C81] font-semibold hover:underline" data-testid="back-to-login-link">
              Sign in.
            </Link>
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="register-form">
            <div>
              <Label className="text-xs font-semibold tracking-wide">Full name</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="mt-2 h-12"
                placeholder="Ishaan Verma"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold tracking-wide">Email</Label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="mt-2 h-12"
                placeholder="you@school.org"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold tracking-wide">Password</Label>
              <Input
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => update("password", e.target.value)}
                className="mt-2 h-12"
                placeholder="At least 6 characters"
                data-testid="register-password-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold tracking-wide">I am signing up as</Label>
              <Select value={form.role} onValueChange={(v) => update("role", v)}>
                <SelectTrigger className="mt-2 h-12" data-testid="register-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="delegate">Delegate</SelectItem>
                  <SelectItem value="org_admin">Event Organiser</SelectItem>
                  <SelectItem value="chair">Chair / Judge</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role !== "org_admin" && orgs.length > 0 && (
              <div>
                <Label className="text-xs font-semibold tracking-wide">Organisation (optional)</Label>
                <Select
                  value={form.organization_id}
                  onValueChange={(v) => update("organization_id", v)}
                >
                  <SelectTrigger className="mt-2 h-12" data-testid="register-org-select">
                    <SelectValue placeholder="Select organisation" />
                  </SelectTrigger>
                  <SelectContent>
                    {orgs.map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-[#0F4C81] hover:bg-[#0A3860] text-white font-semibold uppercase tracking-[0.16em] text-sm"
              data-testid="register-submit-button"
            >
              {submitting ? "Creating…" : "Create account"}
            </Button>
          </form>
          <BrandAttribution className="mt-8" />
        </div>
      </div>

      <div className="relative hidden lg:block order-1 lg:order-2 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1758270705641-acf09b68a91f')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-bl from-[#0A192F] via-[#0F4C81]/85 to-[#D4AF37]/40" />
        <div className="relative h-full p-12 flex flex-col justify-end text-white">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Delegates · Chairs · Organisers</p>
          <h2 className="mt-4 font-serif italic text-4xl leading-tight">"Be moved by the motion."</h2>
          <p className="mt-5 max-w-md text-slate-300">
            Your MUN journey, from first registration to last certificate, under one roof.
          </p>
        </div>
      </div>
    </div>
  );
}
